import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faDownload, faNewspaper, faBook, faFileContract, faMicrophone, faVideo, faSpinner, faFileText } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../api/axios';

const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = String(url).match(
        /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?.*v=|embed\/|v\/|shorts\/|live\/))([A-Za-z0-9_-]{11})/
    );
    return match ? match[1] : null;
};

const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
};

const isVideoType = (name) => String(name || '').toLowerCase().includes('video');
const isAudioType = (name) => String(name || '').toLowerCase().includes('audio');
const isCuttingType = (name) => String(name || '').toLowerCase().includes('cutting');

const ContentCard = ({ data }) => {
    const { t } = useTranslation();
    const tr = (key) => t(`contentCard:${key}`);

    const navigate = useNavigate();

    const [thumbnails, setThumbnails] = React.useState({});
    const [downloadingCards, setDownloadingCards] = React.useState(new Set());

    React.useEffect(() => {
        const fetchThumbnails = async () => {
            const results = await Promise.all(
                data?.map(async (d) => {
                    const isVideo = isVideoType(d.material_type?.name);
                    const isAudio = isAudioType(d.material_type?.name);

                    if ((isVideo || isAudio) && d.url) {
                        const youtubeThumbnail = getYouTubeThumbnail(d.url);
                        if (youtubeThumbnail) {
                            return { id: d.id, thumbnail: youtubeThumbnail };
                        }
                    }

                    // For other content types, use the existing thumbnail logic
                    const uri = d?.thumbnail?.uri;
                    if (!uri) return { id: d.id, thumbnail: null };
                    try {
                        const res = await axiosInstance.get(`/fsthumbnails/${uri}`, {
                            responseType: 'arraybuffer'
                        });
                        const base64 = btoa(new Uint8Array(res.data).reduce((data, byte) => data + String.fromCharCode(byte), ''));
                        return { id: d.id, thumbnail: `data:image/png;base64,${base64}` };
                    } catch {
                        return { id: d.id, thumbnail: null };
                    }
                }) || []
            );
            setThumbnails(Object.fromEntries(results.map(({ id, thumbnail }) => [id, thumbnail])));
        };

        fetchThumbnails();
    }, [data]);

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    const handleRead = (data) => {
        console.log('data', data);
        navigate(`/read`, { state: { id: data.id, documentType: data.material_type.name } });
    };

    const handleDownload = async (data) => {
        setDownloadingCards(prev => new Set(prev).add(data.id));
        try {
            if (!data.manual?.uri) {
                console.error('No manual URI found');
                return;
            }

            const res = await axiosInstance.get(`/fsmanuals/${data.manual.uri}`, {
                responseType: 'blob',
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0'
                }
            });

            // Create a blob URL for the file
            const fileBlob = new Blob([res.data], { type: res.data.type });
            const url = URL.createObjectURL(fileBlob);

            // Create a temporary link element and trigger download
            const link = document.createElement('a');
            link.href = url;
            link.download = data.title || 'document.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the blob URL
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading manual:', error);
        } finally {
            setDownloadingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(data.id);
                return newSet;
            });
        }
    };

    const getIcon = (type) => {
        const materialType = type.toLowerCase();
        if (materialType.includes('journal')) {
            return faNewspaper;
        } else if (materialType.includes('book')) {
            return faBook;
        } else if (materialType.includes('report')) {
            return faFileText;
        } else if (materialType.includes('case') || materialType.includes('court')) {
            return faFileContract;
        } else if (materialType.includes('cutting')) {
            return faNewspaper;
        } else if (materialType.includes('audio')) {
            return faMicrophone;
        } else if (materialType.includes('video')) {
            return faVideo;
        } else {
            return faNewspaper;
        }
    };

    return (
        <motion.div initial="hidden" animate="visible">
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 pt-6">
                {data?.map((d) => {
                    const year = d.date ? new Date(d.date).getFullYear() : 'Unknown';
                    const thumbnailData = thumbnails[d.id];
                    const isVideo = isVideoType(d.material_type?.name);
                    const isAudio = isAudioType(d.material_type?.name);
                    const isCutting = isCuttingType(d.material_type?.name);
                    const canDownload = !isVideo && !isAudio && !isCutting;

                    return (
                        <motion.div
                            key={d.id}
                            variants={cardVariants}
                            whileHover={{ scale: 1.03 }}
                            className="rounded-lg overflow-hidden transition-all duration-300
                                border border-gray-200 dark:border-gray-700
                                bg-white dark:bg-gray-800
                                shadow-lg hover:shadow-xl"
                        >
                            <div className="relative p-4">
                                {/* Journal Icon */}
                                <div className="absolute top-2 right-2 text-gray-500 dark:text-gray-400">
                                    <FontAwesomeIcon icon={getIcon(d.material_type.name)} className="w-5 h-5" />
                                </div>

                                {/* Thumbnail */}
                                {thumbnailData ? (
                                    <div className="w-full h-36 overflow-hidden rounded-lg mb-3 mt-8">
                                        {(isVideo || isAudio) && d.url ? (
                                            // Show embedded YouTube video for video/audio content
                                            <iframe
                                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(d.url)}`}
                                                title={d.title}
                                                className="w-full h-full"
                                                frameBorder="0"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                            />
                                        ) : (
                                            // Show regular image for other content types
                                            <img 
                                                src={thumbnailData} 
                                                alt={d.title} 
                                                className="w-full h-full object-cover bg-gray-100 dark:bg-gray-800 transform hover:scale-105 transition-transform duration-300"
                                                onError={(e) => {
                                                    // Fallback to default icon if image fails to load
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                        )}
                                        <div className="w-full h-36 overflow-hidden rounded-lg mb-3 mt-8 flex items-center justify-center hidden">
                                            <FontAwesomeIcon icon={getIcon(d.material_type.name)} className="w-32 h-32 text-gray-500 dark:text-gray-400" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full h-36 overflow-hidden rounded-lg mb-3 mt-8 flex items-center justify-center">
                                        <FontAwesomeIcon icon={getIcon(d.material_type.name)} className="w-32 h-32 text-gray-500 dark:text-gray-400" />
                                    </div>
                                )}
                                {/* Content */}
                                <div className="mt-4">
                                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 truncate">{d?.title || ''}</h3>
                                    <p
                                        className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed overflow-hidden"
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical'
                                        }}
                                    >
                                        {d?.description || ''}
                                    </p>

                                    {/* Meta Info */}
                                    <div className="flex justify-between items-center mt-3">
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{year}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center justify-between mt-4">
                                    <button className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-500 text-sm flex items-center" onClick={() => handleRead(d)}>
                                        <FontAwesomeIcon icon={faEye} className="w-3 h-3 mr-1" />
                                        {isVideo ? tr('watch') : isAudio ? tr('listen') : tr('read')}
                                    </button>

                                    {canDownload && (
                                        <button 
                                            className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-500 text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed" 
                                            onClick={() => handleDownload(d)}
                                            disabled={downloadingCards.has(d.id)}
                                        >
                                            <FontAwesomeIcon 
                                                icon={downloadingCards.has(d.id) ? faSpinner : faDownload} 
                                                className={`w-3 h-3 mr-1 ${downloadingCards.has(d.id) ? 'animate-spin' : ''}`} 
                                            />
                                            {tr('download')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>
        </motion.div>
    );
};

export default memo(ContentCard);
